import React from 'react';

/**
 * Botaniczne SVG ilustracje dla redesignu Terroir.
 *
 * Wszystkie skalowalne, motywowane przez currentColor i palette tokens.
 * Używać jako placeholdery dla zdjęć roślin lub jako empty state graphics.
 *
 * Konwencja: każda ilustracja przyjmuje { size, primaryColor, secondaryColor, accentColor }
 * i renderuje viewBox 0 0 120 140 (proporcje pionowe).
 */

// ── Pomidor (z owocami i liśćmi) ─────────────────────────────────
export function TomatoPlant({ size = 120, primary = 'var(--t-sage)', accent = 'var(--t-rust)', highlight = 'var(--t-amber)' }) {
  return (
    <svg width={size} height={size * 1.17} viewBox="0 0 120 140" fill="none" aria-hidden="true">
      {/* Łodyga */}
      <path d="M60 130V60" stroke={primary} strokeWidth="2.5" strokeLinecap="round" />
      {/* Liście */}
      <path
        d="M60 60C60 40 40 25 20 25c0 25 15 40 40 40zM60 60c0-15 15-30 35-30 0 18-12 30-35 30zM60 90c0-10 10-20 25-20 0 12-8 20-25 20z"
        stroke={primary} strokeWidth="2" strokeLinejoin="round"
        fill={primary} fillOpacity="0.15"
      />
      {/* Owoce */}
      <circle cx="40" cy="90" r="10" fill={accent} stroke={accent} strokeWidth="1.5" />
      <circle cx="80" cy="100" r="12" fill={accent} stroke={accent} strokeWidth="1.5" />
      <circle cx="55" cy="110" r="8" fill={highlight} stroke={highlight} strokeWidth="1.5" />
    </svg>
  );
}

// ── Sadzonka / sprout (kiełek z dwoma listkami) ──────────────────
export function Sprout({ size = 120, color = 'var(--t-sage)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      {/* Pęd */}
      <path d="M60 110V60" stroke={color} strokeWidth="3" strokeLinecap="round" />
      {/* Lewy listek */}
      <path
        d="M60 60C60 45 45 35 30 35c0 18 12 28 30 25z"
        stroke={color} strokeWidth="2.5" strokeLinejoin="round"
        fill={color} fillOpacity="0.18"
      />
      {/* Prawy listek */}
      <path
        d="M60 60c0-12 12-22 28-22 0 14-9 22-28 22z"
        stroke={color} strokeWidth="2.5" strokeLinejoin="round"
        fill={color} fillOpacity="0.18"
      />
      {/* Gleba */}
      <path d="M30 110h60" stroke={color} strokeWidth="2" strokeLinecap="round" strokeOpacity="0.4" />
      <path d="M40 115l3 -2M55 115l2 -2M70 115l3 -2M85 115l2 -2"
            stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.3" />
    </svg>
  );
}

// ── Bazylia (krzaczek ziołowy z drobnymi listkami) ───────────────
export function HerbBush({ size = 120, color = 'var(--t-sage)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      <path d="M60 115V70" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      {/* Klastry liści */}
      {[
        { cx: 45, cy: 50, r: 10 }, { cx: 60, cy: 35, r: 12 }, { cx: 75, cy: 50, r: 10 },
        { cx: 35, cy: 70, r: 9 },  { cx: 55, cy: 60, r: 8 },  { cx: 70, cy: 70, r: 9 },
        { cx: 85, cy: 65, r: 8 },
      ].map((leaf, i) => (
        <circle key={i} cx={leaf.cx} cy={leaf.cy} r={leaf.r}
                fill={color} fillOpacity="0.25"
                stroke={color} strokeWidth="1.5" />
      ))}
      {/* Doniczka */}
      <path d="M40 115h40l-3 -10h-34z" stroke={color} strokeWidth="2" fill="none" strokeOpacity="0.6" />
    </svg>
  );
}

// ── Marchewka (korzeń + nać) ─────────────────────────────────────
export function Carrot({ size = 120, root = 'var(--t-amber)', leaves = 'var(--t-sage)' }) {
  return (
    <svg width={size} height={size * 1.17} viewBox="0 0 120 140" fill="none" aria-hidden="true">
      {/* Nać */}
      <path d="M60 60V40M52 60V42M68 60V42M44 60V46M76 60V46"
            stroke={leaves} strokeWidth="2" strokeLinecap="round" />
      {/* Korzeń */}
      <path d="M40 60h40l-15 60a8 8 0 01-10 0z"
            fill={root} fillOpacity="0.5" stroke={root} strokeWidth="2" strokeLinejoin="round" />
      {/* Linie pierścieni */}
      <path d="M44 75h32M46 90h28M50 105h20" stroke={root} strokeWidth="1" strokeLinecap="round" strokeOpacity="0.6" />
    </svg>
  );
}

// ── Kwiat (5-płatkowy, np. nagietek) ─────────────────────────────
export function Flower({ size = 120, petals = 'var(--t-amber)', center = 'var(--t-rust)', stem = 'var(--t-sage)' }) {
  return (
    <svg width={size} height={size * 1.17} viewBox="0 0 120 140" fill="none" aria-hidden="true">
      <path d="M60 130V70" stroke={stem} strokeWidth="2.5" strokeLinecap="round" />
      {/* Liść */}
      <path d="M60 100c-8 0-15-5-15-12 7 0 15 5 15 12z"
            fill={stem} fillOpacity="0.3" stroke={stem} strokeWidth="1.5" />
      {/* Płatki — 5 kół rozłożonych dookoła środka */}
      {[0, 72, 144, 216, 288].map((angle, i) => {
        const rad = (angle - 90) * Math.PI / 180;
        const cx = 60 + Math.cos(rad) * 16;
        const cy = 50 + Math.sin(rad) * 16;
        return (
          <ellipse key={i} cx={cx} cy={cy} rx="12" ry="14"
                   fill={petals} fillOpacity="0.55" stroke={petals} strokeWidth="1.5"
                   transform={`rotate(${angle} ${cx} ${cy})`} />
        );
      })}
      {/* Środek */}
      <circle cx="60" cy="50" r="9" fill={center} stroke={center} strokeWidth="1.5" />
    </svg>
  );
}

// ── Grządka (pusty zagon z pasami gleby) — dla empty state ──────
export function EmptyBed({ size = 160, color = 'var(--t-text-mute)' }) {
  return (
    <svg width={size} height={size * 0.6} viewBox="0 0 160 96" fill="none" aria-hidden="true">
      {/* Ramka zagonu */}
      <rect x="6" y="6" width="148" height="84" rx="6"
            stroke={color} strokeWidth="1.5" fill="none" strokeDasharray="4 4" />
      {/* Pasy gleby */}
      {[20, 36, 52, 68].map((y) => (
        <path key={y} d={`M16 ${y}h128`}
              stroke={color} strokeWidth="1" strokeOpacity="0.3" strokeLinecap="round" />
      ))}
      {/* Plus znak */}
      <circle cx="80" cy="48" r="14" fill="var(--t-bg-elev)" stroke={color} strokeWidth="1.5" />
      <path d="M80 42v12M74 48h12" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// ── Pusta księga (empty journal) ─────────────────────────────────
export function EmptyJournal({ size = 140, color = 'var(--t-text-mute)' }) {
  return (
    <svg width={size} height={size * 1.1} viewBox="0 0 140 154" fill="none" aria-hidden="true">
      <path d="M30 16h70l16 14v100a8 8 0 01-8 8H30a8 8 0 01-8-8V24a8 8 0 018-8z"
            stroke={color} strokeWidth="1.5" fill="var(--t-bg-elev)" strokeLinejoin="round" />
      <path d="M100 16v14h16" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      {/* Linie tekstu */}
      <path d="M40 50h60M40 64h60M40 78h40" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeOpacity="0.5" />
      <path d="M40 102h36" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeOpacity="0.3" />
    </svg>
  );
}

// ── Konewka (watering can) ───────────────────────────────────────
export function WateringCan({ size = 120, color = 'var(--t-sage)' }) {
  return (
    <svg width={size} height={size * 0.85} viewBox="0 0 120 102" fill="none" aria-hidden="true">
      {/* Korpus */}
      <path d="M30 30h45v50a8 8 0 01-8 8H38a8 8 0 01-8-8z"
            stroke={color} strokeWidth="2" fill={color} fillOpacity="0.15" strokeLinejoin="round" />
      {/* Uchwyt */}
      <path d="M75 35c10 0 15 8 15 18s-5 18-15 18" stroke={color} strokeWidth="2" fill="none" />
      {/* Wylew */}
      <path d="M30 50l-22 -8v16z" stroke={color} strokeWidth="2" fill={color} fillOpacity="0.3" strokeLinejoin="round" />
      {/* Sito */}
      <circle cx="10" cy="46" r="2" fill={color} />
      <circle cx="14" cy="42" r="1.5" fill={color} />
      <circle cx="14" cy="50" r="1.5" fill={color} />
      {/* Krople */}
      <path d="M5 56l-2 6M3 50l-2 5M7 64l-1 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.7" />
    </svg>
  );
}

// ── Logo Garden App (sprout w pillu) ──────────────────────────────
export function GardenLogo({ size = 32, withText = false, textSize = 22 }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: size,
        height: size,
        borderRadius: size * 0.28,
        background: 'var(--t-sage)',
        display: 'grid',
        placeItems: 'center',
      }}>
        <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 16 16" fill="none">
          <path d="M8 14V8M8 8C8 5 6 3 3 3c0 3 2 5 5 5zM8 8c0-2 1.5-3.5 4-3.5C12 6.5 10.5 8 8 8z"
                stroke="var(--t-bg)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {withText && (
        <span style={{
          fontFamily: 'var(--t-display)',
          fontSize: textSize,
          fontWeight: 300,
          letterSpacing: '-.02em',
          color: 'var(--t-text)',
        }}>
          GardenApp<span style={{ color: 'var(--t-sage)', fontStyle: 'italic' }}>.pl</span>
        </span>
      )}
    </div>
  );
}

// ── Splash hero (do landing / loading screen) ─────────────────────
export function SplashHero({ size = 200 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" fill="none" aria-hidden="true">
      {/* Krąg tła */}
      <circle cx="100" cy="100" r="80" fill="var(--t-sage)" fillOpacity="0.06" />
      <circle cx="100" cy="100" r="60" fill="var(--t-sage)" fillOpacity="0.08" />

      {/* Centralna sadzonka */}
      <g transform="translate(60, 60)">
        <Sprout size={80} color="var(--t-sage)" />
      </g>

      {/* Drobne dekoracyjne kropki orbity */}
      {[0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle - 90) * Math.PI / 180;
        const cx = 100 + Math.cos(rad) * 90;
        const cy = 100 + Math.sin(rad) * 90;
        return <circle key={i} cx={cx} cy={cy} r="2" fill="var(--t-sage)" opacity={0.4 + (i * 0.1)} />;
      })}
    </svg>
  );
}

// Mapa odpowiednich ilustracji dla kategorii rośliny
export function PlantIllustration({ category = 'vegetable', tone = 'sage', size = 120 }) {
  const palettes = {
    sage: { primary: 'var(--t-sage)', accent: 'var(--t-rust)', highlight: 'var(--t-amber)' },
    amber: { primary: 'var(--t-amber)', accent: 'var(--t-rust)', highlight: 'var(--t-sage)' },
    rust: { primary: 'var(--t-rust)', accent: 'var(--t-amber)', highlight: 'var(--t-sage)' },
  };
  const p = palettes[tone] || palettes.sage;

  switch (category) {
    case 'tomato':
    case 'fruit-vegetable':
      return <TomatoPlant size={size} primary={p.primary} accent={p.accent} highlight={p.highlight} />;
    case 'herb':
      return <HerbBush size={size} color={p.primary} />;
    case 'root':
    case 'carrot':
      return <Carrot size={size} root={p.accent} leaves={p.primary} />;
    case 'flower':
      return <Flower size={size} petals={p.primary} center={p.accent} stem={p.highlight} />;
    case 'sprout':
    default:
      return <Sprout size={size} color={p.primary} />;
  }
}

// ============================================================
// BOTANICAL DECORATIONS — używane w designie "herbarz/atlas"
// ============================================================

// Narożnik karty herbarza — 3 stylizowane listki, używany 4× per karta.
// Stosowany w PlantDetail hero (rotacja 0/90/180/270 → 4 rogi).
export function HerbariumCorner({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
      <path d="M2 2 L8 2 M2 2 L2 8" stroke={color} strokeWidth="0.8" strokeLinecap="round" />
      <path
        d="M3 5 Q5 3 7 5 Q5 6 3 5 Z M5 3 Q7 5 5 7 Q4 5 5 3 Z"
        fill={color}
        opacity="0.8"
      />
      <circle cx="3" cy="3" r="0.8" fill={color} />
    </svg>
  );
}

// Rosnący kiełek — używany jako loader i ikona "kwitnienia".
// CSS klasy t-grow-* w tokens.css ożywiają poszczególne ścieżki.
// Set `loop` żeby pulsował po wyrośnięciu (loader); bez = jednorazowo (toast).
export function GrowingSeedling({ size = 64, color = 'var(--t-sage)', loop = true }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
      {/* Gleba */}
      <path d="M8 52 Q32 48 56 52 L56 56 Q32 60 8 56 Z" fill={color} opacity="0.18" />
      {/* Łodyga (rośnie) */}
      <path
        d="M32 52 L32 26"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        className={loop ? 't-grow-loop' : 't-grow-stem'}
      />
      {/* Liść lewy */}
      <path
        d="M32 36 Q22 32 18 26 Q22 26 28 30 Q31 32 32 36 Z"
        fill={color}
        className="t-grow-leaf-l"
      />
      {/* Liść prawy */}
      <path
        d="M32 30 Q42 26 46 20 Q42 20 36 24 Q33 26 32 30 Z"
        fill={color}
        className="t-grow-leaf-r"
      />
    </svg>
  );
}

// Gałązka dla sidebara — 5 listków na wijącej łodydze.
// Klasy t-sprig-idle / t-sprig-pending z tokens.css sterują kolorem/pulsacją.
export function SidebarSprig({ size = 90, pending = false }) {
  const cls = pending ? 't-sprig-pending' : 't-sprig-idle';
  return (
    <svg
      width={size}
      height={Math.round(size * 1.6)}
      viewBox="0 0 90 144"
      fill="none"
      className={cls}
      aria-hidden="true"
    >
      {/* Łodyga */}
      <path
        d="M45 140 Q42 110 48 90 Q40 70 50 50 Q44 30 50 10"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Liście (par przeciwlegle) */}
      <path d="M48 110 Q60 105 64 96 Q56 100 48 110 Z" fill="currentColor" />
      <path d="M44 100 Q32 95 28 86 Q36 90 44 100 Z" fill="currentColor" />
      <path d="M50 78 Q62 72 66 62 Q56 66 50 78 Z" fill="currentColor" />
      <path d="M46 66 Q34 60 30 50 Q40 54 46 66 Z" fill="currentColor" />
      <path d="M50 42 Q60 36 64 26 Q54 30 50 42 Z" fill="currentColor" />
      {/* Pączek na czubku */}
      <circle cx="50" cy="10" r="2.5" fill="currentColor" />
    </svg>
  );
}

// Bloom burst — radialny rozbłysk po klik akcji.
// Używany jako overlay na przyciskach (position: absolute na nadrzędnym relative).
export function BloomBurstOverlay() {
  return <span className="t-bloom-burst" aria-hidden="true" />;
}
